import Colors from '@dynatrace/strato-design-tokens/colors';

import { SCALE_FALLBACK_COLOR } from '../core/constants.js';
import { type LegendPosition } from '../core/types/legend.js';
import type { TextSize } from '../core/types/text-size.js';
import { HoneycombRequiredConfigProps } from './types/honeycomb-internal.js';
import {
  type HoneycombDatasetType,
  type HoneycombLabelsDisplay,
} from './types/honeycomb.js';
import { HiveTileBestFitProps } from './types/rendering.js';
import { HoneycombState } from './types/state.js';

export const DISPLAY_NAME = 'HoneycombChart';

export const DEFAULT_DATASET_TYPE: HoneycombDatasetType = 'numeric';

export const DEFAULT_HONEYCOMB_WIDTH = '100%';
export const DEFAULT_HONEYCOMB_HEIGHT = 300;
export const DEFAULT_HONEYCOMB_SHAPE = 'hexagon';
export const DEFAULT_HONEYCOMB_TILE_COLOR = SCALE_FALLBACK_COLOR;
export const DEFAULT_HONEYCOMB_COLOR_PALETTE = 'blue';
export const DEFAULT_HONEYCOMB_CATEGORICAL_COLOR_PALETTE = 'categorical';

export const HONEYCOMB_TILE_STROKE_COLOR = Colors.Theme.Background['10'];

/**
 * Amount of height considered from the second line on,
 * because a 25% overlaps with the previous line.
 * example: hiveHeight =\> 1st line = 1, 2nd line = 1.75
 */
export const NON_OVERLAPPING_HEIGHT_HEXS = 0.75;
/**
 * Amount of height considered from the second line on,
 * because a 14% overlaps with the previous line.
 * example: hiveHeight =\> 1st line = 1, 2nd line = 1.86
 */
export const NON_OVERLAPPING_HEIGHT_CIRCLES = 0.86;
const NON_OVERLAPPING_HEIGHT_SQUARES = 1;

export const NON_OVERLAPPING_HEIGHT = {
  circle: NON_OVERLAPPING_HEIGHT_CIRCLES,
  square: NON_OVERLAPPING_HEIGHT_SQUARES,
  hexagon: NON_OVERLAPPING_HEIGHT_HEXS,
};

export const NOT_HOVERED_TILE_OPACITY = 0.2;
export const HOVERED_TILE_OPACITY = 1;

export const DEFAULT_LEGEND_POSITION: LegendPosition = 'auto';

export const DEFAULT_HONEYCOMB_LABELS_DISPLAY: HoneycombLabelsDisplay = 'none';

export const MAX_GAP_SIZE = 16;
export const HONEYCOMB_LABEL_FONT_SIZE = 12;
export const HONEYCOMB_VALUE_LABEL_FONT_SIZE = 16;
export const HONEYCOMB_LABEL_FONT_WEIGHT = 400;
export const HONEYCOMB_VALUE_FONT_WEIGHT = 500;
export const HONEYCOMB_LABEL_LINE_HEIGHT = 20;
export const HONEYCOMB_VALUE_LINE_HEIGHT = 28;
export const HONEYCOMB_LABEL_FONT_SIZE_OFFSET = 4;
export const HONEYCOMB_VALUE_LINE_HEIGHT_RATIO = 0.5;
export const HONEYCOMB_DEFAULT_TEXT_ALIGNMENT = 'center';
export const HONEYCOMB_DEFAULT_MIN_ELLIPSIZED_TEXT = 'w...w';
export const HONEYCOMB_CHART_ARIA_LABEL = 'Honeycomb Chart';

export const DEFAULT_TEXT_SIZE: TextSize = HONEYCOMB_VALUE_LABEL_FONT_SIZE;

export const DEFAULT_HONEYCOMB_CONFIG: HoneycombRequiredConfigProps = {
  shape: DEFAULT_HONEYCOMB_SHAPE,
  colorScheme: DEFAULT_HONEYCOMB_COLOR_PALETTE,
  toolbar: undefined,
  legend: {},
  formatter: {},
  unit: undefined,
  truncationMode: 'middle',
  min: undefined,
  max: undefined,
  labelsDisplay: DEFAULT_HONEYCOMB_LABELS_DISPLAY,
  textSize: DEFAULT_TEXT_SIZE,
  colorRules: [],
};

export const DEFAULT_TILE_NAME = 'node';

export const initialState: HoneycombState = {
  selection: {
    hovered: undefined,
  },
};

export const bestFitInitialState: HiveTileBestFitProps = {
  hiveHeight: 0,
  tileWidth: 0,
  gap: 0,
};
